import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Star, MapPin, DollarSign, Briefcase, Award, Calendar, MessageCircle, Flame, Repeat, Clock, Crown, ShieldCheck, Home, AlertCircle, Gift, X } from 'lucide-react';
import Header from '../components/Header';
import { ALL_TIME_SLOTS, endTimesFor, formatTime12, hoursBetween } from '../utils/timeSlots';
import { getPromoCode, clearPromoCode } from '../hooks/useReferralTracker';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CleanerProfilePage = ({ user }) => {
  const { cleanerId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [cleaner, setCleaner] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [surge, setSurge] = useState(null);
  const [zipRate, setZipRate] = useState(null);
  const [tier, setTier] = useState(null);
  const [platformSettings, setPlatformSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  const zipCode = searchParams.get('zip');
  const serviceDate = searchParams.get('date');
  const serviceTime = searchParams.get('time');
  const endTimeParam = searchParams.get('end');

  const [bookingData, setBookingData] = useState({
    service_type: 'Regular Cleaning',
    start_time: serviceTime || '10:00',
    end_time: endTimeParam || '12:00',
    is_recurring: false,
    recurrence_pattern: 'weekly',
    recurrence_count: 4,
    supplies_provider: null,  // null => use cleaner default; user can override
    flexible: false,
    flexible_window_start: '09:00',
    flexible_window_end: '15:00',
    duration_hours: 2.5,
  });

  const [places, setPlaces] = useState([]);
  const [selectedPlaceId, setSelectedPlaceId] = useState(null);
  const [placesLoading, setPlacesLoading] = useState(false);
  const [nudge, setNudge] = useState(null);  // {peak, suggestion} | null

  // Promo code (auto-captured from ?promo= on landing, or manually entered)
  const [promoCode, setPromoCode] = useState(() => getPromoCode() || '');
  const [promoValidation, setPromoValidation] = useState(null); // {valid, discount, message}
  const [promoValidating, setPromoValidating] = useState(false);

  // Auto-validate the captured promo once the user is loaded
  useEffect(() => {
    if (!user || user.role !== 'customer' || !promoCode || promoValidation) return;
    const validate = async () => {
      setPromoValidating(true);
      try {
        const token = localStorage.getItem('token');
        const r = await axios.post(
          `${API}/promo/validate`,
          { code: promoCode, booking_amount: 100 }, // validity probe; real discount computed server-side at booking
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setPromoValidation({ valid: true, ...r.data });
      } catch (e) {
        setPromoValidation({ valid: false, message: e.response?.data?.detail || 'Invalid code' });
      } finally {
        setPromoValidating(false);
      }
    };
    validate();
  }, [user, promoCode, promoValidation]);

  const removePromo = () => {
    setPromoCode('');
    setPromoValidation(null);
    clearPromoCode();
  };

  // Load customer's saved places (only when signed in as customer)
  useEffect(() => {
    if (!user || user.role !== 'customer') return;
    const loadPlaces = async () => {
      setPlacesLoading(true);
      try {
        const token = localStorage.getItem('token');
        const r = await axios.get(`${API}/places`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPlaces(r.data || []);
        const list = r.data || [];
        const fromUrl = searchParams.get('place_id');
        const target =
          (fromUrl && list.find(p => p.id === fromUrl)) ||
          list.find(p => p.is_default) ||
          list[0];
        setSelectedPlaceId(target ? target.id : null);
      } catch (e) {
        console.error('Failed to load places', e);
      } finally {
        setPlacesLoading(false);
      }
    };
    loadPlaces();
  }, [user]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [cleanerRes, reviewsRes] = await Promise.all([
          axios.get(`${API}/cleaners/${cleanerId}`),
          axios.get(`${API}/reviews/cleaner/${cleanerId}`)
        ]);
        setCleaner(cleanerRes.data);
        setReviews(reviewsRes.data);
        
        // Fetch surge and zip rate for the cleaner's zip
        if (cleanerRes.data?.zip_code) {
          try {
            const [surgeRes, rateRes, tierRes, settingsRes] = await Promise.all([
              axios.get(`${API}/zip/${cleanerRes.data.zip_code}/demand`),
              axios.get(`${API}/zip-rates/${cleanerRes.data.zip_code}`),
              axios.get(`${API}/cleaners/${cleanerId}/tier`),
              axios.get(`${API}/platform/settings`)
            ]);
            setSurge(surgeRes.data);
            setZipRate(rateRes.data?.base_rate || null);
            setTier(tierRes.data);
            setPlatformSettings(settingsRes.data);
          } catch (e) {
            console.error('Surge/rate/tier fetch failed', e);
          }
        }
      } catch (error) {
        console.error('Failed to fetch cleaner data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [cleanerId]);

  // Booking-nudge: detect peak start_time and suggest off-peak savings
  useEffect(() => {
    if (!cleaner?.zip_code || !bookingData.start_time || !bookingData.end_time || bookingData.flexible) {
      setNudge(null);
      return;
    }
    const baseRateNow = Math.max(cleaner.hourly_rate || 0, zipRate || 0);
    const effectiveRateNow = baseRateNow * (tier?.multiplier || 1.0);
    const ctrl = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/bookings/nudge`, {
          params: {
            zip_code: cleaner.zip_code,
            start_time: bookingData.start_time,
            end_time: bookingData.end_time,
            hourly_rate: effectiveRateNow,
          },
          signal: ctrl.signal,
        });
        setNudge(r.data?.peak ? r.data : null);
      } catch (e) {
        if (!axios.isCancel(e)) setNudge(null);
      }
    }, 400);
    return () => { clearTimeout(timer); ctrl.abort(); };
  }, [cleaner?.zip_code, cleaner?.hourly_rate, zipRate, tier?.multiplier, bookingData.start_time, bookingData.end_time, bookingData.flexible]);

  const handleBooking = async () => {
    if (!user) {
      navigate('/auth');
      return;
    }

    if (user.role !== 'customer') {
      alert('Only customers can book cleaners');
      return;
    }

    if (!selectedPlaceId) {
      alert('Please add and select a service address before booking.');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/bookings`,
        {
          cleaner_id: cleanerId,
          service_date: serviceDate,
          service_time: bookingData.flexible ? bookingData.flexible_window_start : bookingData.start_time,
          start_time: bookingData.flexible ? null : bookingData.start_time,
          end_time: bookingData.flexible ? null : bookingData.end_time,
          service_type: bookingData.service_type,
          place_id: selectedPlaceId,
          is_recurring: bookingData.is_recurring,
          recurrence_pattern: bookingData.is_recurring ? bookingData.recurrence_pattern : null,
          recurrence_count: bookingData.is_recurring ? bookingData.recurrence_count : 1,
          supplies_provider: bookingData.supplies_provider,
          flexible: bookingData.flexible,
          flexible_window_start: bookingData.flexible ? bookingData.flexible_window_start : null,
          flexible_window_end: bookingData.flexible ? bookingData.flexible_window_end : null,
          duration_hours: bookingData.flexible ? bookingData.duration_hours : null,
          promo_code: promoValidation?.valid ? promoCode : null,
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const booking = response.data;
      
      // Initiate payment
      const paymentResponse = await axios.post(
        `${API}/payments/checkout/session`,
        {
          booking_id: booking.id,
          origin_url: window.location.origin
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      window.location.href = paymentResponse.data.url;
    } catch (error) {
      console.error('Booking failed:', error);
      alert(error.response?.data?.detail || 'Booking failed. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header user={user} setUser={() => {}} />
        <div className="flex items-center justify-center py-24">
          <p className="text-text-secondary">Loading...</p>
        </div>
      </div>
    );
  }

  if (!cleaner) {
    return (
      <div className="min-h-screen bg-background">
        <Header user={user} setUser={() => {}} />
        <div className="flex items-center justify-center py-24">
          <p className="text-text-secondary">Cleaner not found</p>
        </div>
      </div>
    );
  }

  const durationHours = hoursBetween(bookingData.start_time, bookingData.end_time);
  const baseRate = Math.max(cleaner.hourly_rate, zipRate || 0);
  const tierMultiplier = tier?.multiplier || 1.0;
  const effectiveRate = Math.round(baseRate * tierMultiplier * 100) / 100;
  const baseCost = effectiveRate * durationHours;
  const surgeMultiplier = surge?.multiplier || 1.0;

  // Time-of-day peak multiplier (mirror of backend logic in calculate_time_of_day_multiplier)
  const _toMin = (hhmm) => {
    if (!hhmm || !/^\d{2}:\d{2}$/.test(hhmm)) return null;
    const [h, m] = hhmm.split(':').map(Number);
    return h * 60 + m;
  };
  const _peakHit = (windowKey) => {
    const mult = parseFloat(platformSettings?.[`peak_${windowKey}_multiplier`] ?? 1.0);
    if (!(mult > 1.0)) return null;
    const ws = _toMin(platformSettings?.[`peak_${windowKey}_start`]);
    const we = _toMin(platformSettings?.[`peak_${windowKey}_end`]);
    const sm = _toMin(bookingData.start_time);
    if (ws === null || we === null || sm === null || we <= ws) return null;
    if (sm >= ws && sm < we) return { multiplier: mult, label: windowKey === 'morning' ? 'Morning peak' : 'Evening peak' };
    return null;
  };
  const peakHit = _peakHit('morning') || _peakHit('evening');
  const timeOfDayMultiplier = peakHit?.multiplier || 1.0;
  const subtotalCost = Math.round(baseCost * surgeMultiplier * timeOfDayMultiplier * 100) / 100;

  // Supplies: customer can override the cleaner's default. Surcharge only applies when
  // cleaner's default is "customer" and customer asks them to bring supplies.
  const cleanerSuppliesDefault = cleaner.supplies_default || 'cleaner';
  const effectiveSuppliesProvider = bookingData.supplies_provider || cleanerSuppliesDefault;
  const suppliesSurchargeRate = platformSettings?.supplies_surcharge ?? 12.0;
  const suppliesSurcharge = (effectiveSuppliesProvider === 'cleaner' && cleanerSuppliesDefault === 'customer')
    ? suppliesSurchargeRate
    : 0;
  const totalCost = Math.round((subtotalCost + suppliesSurcharge) * 100) / 100;

  return (
    <div className="min-h-screen bg-background">
      <Header user={user} setUser={() => {}} />
      
      <div className="px-6 md:px-12 lg:px-16 py-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2">
              {/* Profile Header */}
              <div className="bg-surface rounded-2xl p-8 border border-border mb-6" data-testid="cleaner-profile-header">
                <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
                  <div>
                    <div className="flex items-center gap-3 flex-wrap mb-2">
                      <h1 className="font-heading text-3xl font-bold text-text-primary">{cleaner.name}</h1>
                      {tier && tier.name !== 'Standard' && (
                        <span
                          className="inline-flex items-center gap-1.5 px-3 py-1 text-white text-xs font-bold rounded-full uppercase tracking-wider"
                          style={{ backgroundColor: tier.color }}
                          data-testid="tier-badge-profile"
                        >
                          <Crown className="w-3.5 h-3.5" />
                          {tier.name}
                        </span>
                      )}
                      {cleaner.bg_check_status === 'clear' && (
                        <span
                          className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-500 text-white text-xs font-bold rounded-full"
                          data-testid="bg-check-verified-badge"
                          title="This cleaner passed a Checkr background check"
                        >
                          <ShieldCheck className="w-3.5 h-3.5" />
                          Background-checked
                        </span>
                      )}
                      <span
                        className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-semibold rounded-full ${cleaner.supplies_default === 'cleaner' ? 'bg-primary-light text-primary' : 'bg-amber-50 text-amber-800 border border-amber-200'}`}
                        data-testid="supplies-default-badge"
                      >
                        {cleaner.supplies_default === 'cleaner' ? 'Brings own supplies' : 'Customer provides supplies'}
                      </span>
                    </div>
                    {tier && tier.name !== 'Standard' && (
                      <p className="text-xs text-text-secondary mb-2 italic">{tier.description}</p>
                    )}
                    <div className="flex items-center gap-4 text-text-secondary">
                      <div className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        <span>{cleaner.zip_code}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Briefcase className="w-4 h-4" />
                        <span>{cleaner.years_experience} years</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 bg-primary-light px-4 py-2 rounded-full">
                    <Star className="w-5 h-5 text-primary fill-primary" />
                    <span className="font-heading text-xl font-bold text-primary">
                      {cleaner.rating_avg > 0 ? cleaner.rating_avg.toFixed(1) : 'New'}
                    </span>
                    {cleaner.total_reviews > 0 && (
                      <span className="text-sm text-text-secondary">({cleaner.total_reviews})</span>
                    )}
                  </div>
                </div>

                <div className="mb-6">
                  <h3 className="font-heading text-lg font-semibold text-text-primary mb-3">About</h3>
                  <p className="text-text-secondary leading-relaxed">{cleaner.bio}</p>
                </div>

                <div className="mb-6">
                  <h3 className="font-heading text-lg font-semibold text-text-primary mb-3">Services Offered</h3>
                  <div className="flex flex-wrap gap-2">
                    {cleaner.services.map((service, idx) => (
                      <span key={idx} className="px-4 py-2 bg-primary-light text-primary rounded-full font-medium">
                        {service}
                      </span>
                    ))}
                  </div>
                </div>

                {cleaner.certifications && cleaner.certifications.length > 0 && (
                  <div>
                    <h3 className="font-heading text-lg font-semibold text-text-primary mb-3">Certifications</h3>
                    <div className="flex flex-wrap gap-2">
                      {cleaner.certifications.map((cert, idx) => (
                        <span key={idx} className="flex items-center gap-2 px-4 py-2 bg-surface-muted text-text-secondary rounded-xl">
                          <Award className="w-4 h-4" />
                          {cert}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Reviews */}
              <div className="bg-surface rounded-2xl p-8 border border-border">
                <h3 className="font-heading text-2xl font-semibold text-text-primary mb-6">Reviews</h3>
                {reviews.length === 0 ? (
                  <p className="text-text-secondary">No reviews yet.</p>
                ) : (
                  <div className="space-y-6">
                    {reviews.map((review) => (
                      <div key={review.id} className="border-b border-border pb-6 last:border-0" data-testid="review-item">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-semibold text-text-primary">{review.customer_name}</span>
                          <div className="flex items-center gap-1">
                            {[...Array(5)].map((_, i) => (
                              <Star
                                key={i}
                                className={`w-4 h-4 ${
                                  i < review.rating ? 'text-primary fill-primary' : 'text-gray-300'
                                }`}
                              />
                            ))}
                          </div>
                        </div>
                        <p className="text-text-secondary text-sm mb-2">{review.comment}</p>
                        <span className="text-xs text-text-secondary">
                          {new Date(review.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Booking Card */}
            <div className="lg:col-span-1">
              <div className="bg-surface rounded-2xl p-6 border border-border sticky top-24" data-testid="booking-card">
                <div className="flex items-center gap-2 mb-6">
                  <DollarSign className="w-6 h-6 text-primary" />
                  <span className="font-heading text-3xl font-bold text-primary">{cleaner.hourly_rate}</span>
                  <span className="text-text-secondary">/hour</span>
                </div>

                <div className="space-y-4 mb-6">
                  {/* Place selector — required */}
                  {user && user.role === 'customer' && (
                    <div data-testid="booking-place-section">
                      <label className="block text-sm font-medium text-text-secondary mb-2">
                        Service address <span className="text-error">*</span>
                      </label>
                      {placesLoading ? (
                        <p className="text-xs text-text-secondary">Loading addresses…</p>
                      ) : places.length === 0 ? (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3 text-sm" data-testid="no-places-warning">
                          <div className="flex items-start gap-2">
                            <AlertCircle className="w-4 h-4 text-yellow-700 shrink-0 mt-0.5" />
                            <div className="flex-1">
                              <p className="text-yellow-800 font-medium mb-1">You don&apos;t have a saved address yet.</p>
                              <p className="text-yellow-700 text-xs mb-2">Add one (house, office, warehouse, etc.) before you can book.</p>
                              <Link
                                to="/dashboard/customer"
                                className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
                                data-testid="add-place-cta"
                              >
                                <Home className="w-3 h-3" />
                                Add address in My Places
                              </Link>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <select
                          value={selectedPlaceId || ''}
                          onChange={(e) => setSelectedPlaceId(e.target.value)}
                          className="input-field w-full"
                          data-testid="booking-place-select"
                        >
                          {places.map((p) => (
                            <option key={p.id} value={p.id}>
                              {`${p.label} • ${p.property_type} — ${p.street_address}${p.unit ? `, ${p.unit}` : ''}, ${p.zip_code}`}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">Service Type</label>
                    <select
                      value={bookingData.service_type}
                      onChange={(e) => setBookingData({...bookingData, service_type: e.target.value})}
                      className="input-field w-full"
                      data-testid="service-type-select"
                    >
                      <option>Regular Cleaning</option>
                      <option>Deep Cleaning</option>
                      <option>Move In/Out</option>
                      <option>Commercial</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">Service Time</label>
                    {/* Flexible mode toggle */}
                    <div className="flex items-center gap-2 bg-surface-muted rounded-xl p-1 mb-3" role="group" data-testid="booking-mode-toggle">
                      <button
                        type="button"
                        onClick={() => setBookingData({ ...bookingData, flexible: false })}
                        className={`flex-1 py-2 text-xs font-semibold rounded-lg transition-colors ${
                          !bookingData.flexible ? 'bg-white text-text-primary shadow-sm' : 'text-text-secondary'
                        }`}
                        data-testid="mode-fixed"
                      >
                        Fixed time
                      </button>
                      <button
                        type="button"
                        onClick={() => setBookingData({ ...bookingData, flexible: true })}
                        className={`flex-1 py-2 text-xs font-semibold rounded-lg transition-colors ${
                          bookingData.flexible ? 'bg-white text-text-primary shadow-sm' : 'text-text-secondary'
                        }`}
                        data-testid="mode-flexible"
                      >
                        Flexible window
                      </button>
                    </div>
                    
                    {bookingData.flexible ? (
                      <div className="space-y-3" data-testid="flexible-window-form">
                        <p className="text-xs text-text-secondary">
                          Tell us a window and how long you need — your cleaner will pick the exact start time.
                        </p>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-[10px] font-bold uppercase tracking-wider text-text-secondary mb-1">Window start</label>
                            <select
                              value={bookingData.flexible_window_start}
                              onChange={(e) => setBookingData({ ...bookingData, flexible_window_start: e.target.value })}
                              className="input-field w-full"
                              data-testid="flexible-window-start"
                            >
                              {ALL_TIME_SLOTS.map(t => (
                                <option key={t} value={t}>{formatTime12(t)}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-[10px] font-bold uppercase tracking-wider text-text-secondary mb-1">Window end</label>
                            <select
                              value={bookingData.flexible_window_end}
                              onChange={(e) => setBookingData({ ...bookingData, flexible_window_end: e.target.value })}
                              className="input-field w-full"
                              data-testid="flexible-window-end"
                            >
                              {endTimesFor(bookingData.flexible_window_start).map(t => (
                                <option key={t} value={t}>{formatTime12(t)}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div>
                          <label className="block text-[10px] font-bold uppercase tracking-wider text-text-secondary mb-1">How long do you need?</label>
                          <select
                            value={bookingData.duration_hours}
                            onChange={(e) => setBookingData({ ...bookingData, duration_hours: parseFloat(e.target.value) })}
                            className="input-field w-full"
                            data-testid="flexible-duration"
                          >
                            {[1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, 6, 7, 8].map(h => (
                              <option key={h} value={h}>{h} hour{h === 1 ? '' : 's'}</option>
                            ))}
                          </select>
                        </div>
                        <p className="text-xs text-text-secondary">
                          Total: <span className="font-semibold text-primary">{bookingData.duration_hours} hours</span>
                        </p>
                      </div>
                    ) : (
                      <>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="relative">
                        <Clock className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-text-secondary" />
                        <select
                          value={bookingData.start_time}
                          onChange={(e) => {
                            const v = e.target.value;
                            setBookingData({
                              ...bookingData,
                              start_time: v,
                              end_time: bookingData.end_time <= v ? '' : bookingData.end_time
                            });
                          }}
                          className="input-field with-icon w-full"
                          data-testid="profile-start-time"
                        >
                          <option value="">From</option>
                          {ALL_TIME_SLOTS.map(t => (
                            <option key={t} value={t}>{formatTime12(t)}</option>
                          ))}
                        </select>
                      </div>
                      <div className="relative">
                        <Clock className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-text-secondary" />
                        <select
                          value={bookingData.end_time}
                          onChange={(e) => setBookingData({...bookingData, end_time: e.target.value})}
                          className="input-field with-icon w-full"
                          disabled={!bookingData.start_time}
                          data-testid="profile-end-time"
                        >
                          <option value="">To</option>
                          {endTimesFor(bookingData.start_time).map(t => (
                            <option key={t} value={t}>{formatTime12(t)}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    {durationHours > 0 && (
                      <p className="text-xs text-text-secondary mt-2">
                        Duration: <span className="font-semibold text-primary">{durationHours} {durationHours === 1 ? 'hour' : 'hours'}</span>
                      </p>
                    )}
                    {nudge?.peak && nudge?.suggestion && (
                      <div
                        className="mt-3 p-3 rounded-xl border border-amber-200 bg-amber-50 text-amber-900 text-xs flex items-start gap-2"
                        data-testid="booking-nudge"
                      >
                        <Clock className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <div className="flex-1">
                          <p className="font-semibold mb-1">
                            {nudge.peak.label} pricing (+{nudge.peak.premium_percent}%)
                          </p>
                          <p className="leading-snug">
                            Start at <strong>{formatTime12(nudge.suggestion.start_time)}</strong> instead and save{' '}
                            <strong>${nudge.suggestion.savings.toFixed(2)}</strong>.
                          </p>
                          <button
                            type="button"
                            onClick={() => setBookingData({
                              ...bookingData,
                              start_time: nudge.suggestion.start_time,
                              end_time: nudge.suggestion.end_time,
                            })}
                            className="mt-2 inline-flex items-center text-amber-900 underline underline-offset-2 font-semibold hover:text-amber-700"
                            data-testid="booking-nudge-apply"
                          >
                            Switch to off-peak →
                          </button>
                        </div>
                      </div>
                    )}
                      </>
                    )}
                  </div>

                  {/* Recurring Booking */}
                  <div className="border border-border rounded-xl p-4 bg-surface-muted/50">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={bookingData.is_recurring}
                        onChange={(e) => setBookingData({...bookingData, is_recurring: e.target.checked})}
                        className="w-5 h-5 accent-primary cursor-pointer"
                        data-testid="recurring-checkbox"
                      />
                      <div className="flex items-center gap-2">
                        <Repeat className="w-4 h-4 text-primary" />
                        <span className="font-medium text-text-primary text-sm">Make this a recurring booking</span>
                      </div>
                    </label>

                    {bookingData.is_recurring && (
                      <div className="mt-4 space-y-3" data-testid="recurring-options">
                        <div>
                          <label className="block text-xs font-medium text-text-secondary mb-2">Frequency</label>
                          <div className="grid grid-cols-3 gap-2">
                            {['weekly', 'biweekly', 'monthly'].map(p => (
                              <button
                                key={p}
                                type="button"
                                onClick={() => setBookingData({...bookingData, recurrence_pattern: p})}
                                className={`py-2 px-3 rounded-lg text-xs font-semibold transition-all ${
                                  bookingData.recurrence_pattern === p
                                    ? 'bg-primary text-white'
                                    : 'bg-white text-text-secondary border border-border hover:border-primary'
                                }`}
                                data-testid={`recurrence-${p}`}
                              >
                                {p === 'weekly' ? 'Weekly' : p === 'biweekly' ? 'Bi-weekly' : 'Monthly'}
                              </button>
                            ))}
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-text-secondary mb-2">
                            Additional occurrences ({bookingData.recurrence_count})
                          </label>
                          <input
                            type="range"
                            min="1"
                            max="12"
                            value={bookingData.recurrence_count}
                            onChange={(e) => setBookingData({...bookingData, recurrence_count: parseInt(e.target.value)})}
                            className="w-full accent-primary"
                            data-testid="recurrence-count-slider"
                          />
                          <p className="text-xs text-text-secondary mt-1">
                            {bookingData.recurrence_count + 1} total bookings ({bookingData.recurrence_pattern})
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="bg-surface-muted rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Calendar className="w-4 h-4 text-text-secondary" />
                      <span className="text-sm text-text-secondary">Booking Details</span>
                    </div>
                    <p className="text-sm text-text-primary mb-1">{serviceDate} at {serviceTime}</p>
                    <p className="text-sm text-text-secondary">Location: {zipCode}</p>
                  </div>

                  <div data-testid="supplies-choice">
                    <label className="block text-sm font-medium text-text-primary mb-2">Cleaning Supplies</label>
                    <p className="text-xs text-text-secondary mb-3">
                      {cleanerSuppliesDefault === 'cleaner'
                        ? `${cleaner.name} brings their own supplies by default. You can offer to provide instead.`
                        : `${cleaner.name} expects the customer to provide supplies. You can request them to bring (+$${suppliesSurchargeRate.toFixed(2)}).`}
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => setBookingData({...bookingData, supplies_provider: 'cleaner'})}
                        className={`text-left p-3 rounded-xl border-2 transition-all ${effectiveSuppliesProvider === 'cleaner' ? 'border-primary bg-primary-light/40' : 'border-border hover:border-primary/40'}`}
                        data-testid="supplies-choice-cleaner"
                      >
                        <div className="font-semibold text-sm text-text-primary">Cleaner brings</div>
                        <div className="text-[11px] text-text-secondary">
                          {cleanerSuppliesDefault === 'customer' ? `+$${suppliesSurchargeRate.toFixed(2)}` : 'Included'}
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={() => setBookingData({...bookingData, supplies_provider: 'customer'})}
                        className={`text-left p-3 rounded-xl border-2 transition-all ${effectiveSuppliesProvider === 'customer' ? 'border-primary bg-primary-light/40' : 'border-border hover:border-primary/40'}`}
                        data-testid="supplies-choice-customer"
                      >
                        <div className="font-semibold text-sm text-text-primary">I&apos;ll provide</div>
                        <div className="text-[11px] text-text-secondary">No surcharge</div>
                      </button>
                    </div>
                  </div>
                </div>

                <div className="border-t border-border pt-4 mb-6">
                  <div className="flex items-center justify-between mb-2 text-sm">
                    <span className="text-text-secondary">
                      ${effectiveRate.toFixed(2)}/hr × {durationHours}h
                    </span>
                    <span className="font-semibold text-text-primary">${baseCost.toFixed(2)}</span>
                  </div>
                  {zipRate && zipRate > cleaner.hourly_rate && (
                    <div className="flex items-center justify-between mb-2 text-xs text-orange-700">
                      <span>Zip {zipCode} floor rate applied (${zipRate.toFixed(2)}/hr)</span>
                    </div>
                  )}
                  {tier && tier.name !== 'Standard' && (
                    <div className="flex items-center justify-between mb-2 text-xs" style={{ color: tier.color }}>
                      <span className="flex items-center gap-1">
                        <Crown className="w-3 h-3" />
                        {tier.name} tier (+{Math.round((tier.multiplier - 1) * 100)}%)
                      </span>
                    </div>
                  )}
                  {surge && surge.multiplier > 1 && (
                    <div className="flex items-center justify-between mb-2 text-orange-700">
                      <span className="flex items-center gap-1 text-sm">
                        <Flame className="w-4 h-4" />
                        {surge.label} surge (+{surge.premium_percent}%)
                      </span>
                      <span className="font-semibold text-sm">+${(baseCost * (surgeMultiplier - 1)).toFixed(2)}</span>
                    </div>
                  )}
                  {peakHit && (
                    <div className="flex items-center justify-between mb-2 text-amber-700" data-testid="peak-pricing-row">
                      <span className="flex items-center gap-1 text-sm">
                        <Clock className="w-4 h-4" />
                        {peakHit.label} (+{Math.round((peakHit.multiplier - 1) * 100)}%)
                      </span>
                      <span className="font-semibold text-sm">+${(baseCost * surgeMultiplier * (timeOfDayMultiplier - 1)).toFixed(2)}</span>
                    </div>
                  )}
                  {suppliesSurcharge > 0 && (
                    <div className="flex items-center justify-between mb-2 text-text-secondary text-sm" data-testid="supplies-surcharge-row">
                      <span>Supplies (cleaner brings)</span>
                      <span className="font-semibold">+${suppliesSurcharge.toFixed(2)}</span>
                    </div>
                  )}

                  {/* Promo code block */}
                  {user && user.role === 'customer' && (
                    <div className="my-3 pt-3 border-t border-border" data-testid="promo-block">
                      {promoValidation?.valid ? (
                        (() => {
                          const dv = parseFloat(promoValidation.discount_value || 0);
                          const isPercent = promoValidation.discount_type === 'percent';
                          const discountAmount = isPercent ? (totalCost * dv) / 100 : Math.min(dv, totalCost);
                          return (
                            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-start justify-between gap-3" data-testid="promo-applied">
                              <div className="flex items-start gap-2">
                                <Gift className="w-4 h-4 text-emerald-700 mt-0.5 flex-shrink-0" />
                                <div>
                                  <p className="text-sm font-semibold text-emerald-900">
                                    {promoCode} applied — saving ${discountAmount.toFixed(2)}
                                  </p>
                                  <p className="text-xs text-emerald-800">
                                    {isPercent ? `${dv}% off` : `$${dv} off`}
                                    {promoValidation.first_booking_only ? ' · first booking only' : ''}
                                  </p>
                                </div>
                              </div>
                              <button
                                onClick={removePromo}
                                className="text-emerald-700 hover:text-emerald-900 p-1"
                                title="Remove promo"
                                data-testid="promo-remove"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          );
                        })()
                      ) : promoValidation && !promoValidation.valid ? (
                        <div className="bg-rose-50 border border-rose-200 rounded-lg p-2 text-xs text-rose-800 flex items-center justify-between" data-testid="promo-invalid">
                          <span>Promo &ldquo;{promoCode}&rdquo;: {promoValidation.message}</span>
                          <button onClick={removePromo} className="text-rose-700 hover:text-rose-900 underline">Clear</button>
                        </div>
                      ) : (
                        <details className="text-sm">
                          <summary className="cursor-pointer text-text-secondary hover:text-primary inline-flex items-center gap-1">
                            <Gift className="w-3.5 h-3.5" /> Have a promo code?
                          </summary>
                          <div className="mt-2 flex gap-2">
                            <input
                              type="text"
                              value={promoCode}
                              onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                              placeholder="EARLY20"
                              className="input-field flex-1 font-mono uppercase"
                              data-testid="promo-input"
                            />
                            <button
                              onClick={() => setPromoValidation(null)}
                              disabled={!promoCode || promoValidating}
                              className="btn-secondary text-sm disabled:opacity-50"
                              data-testid="promo-apply-btn"
                            >
                              {promoValidating ? 'Checking…' : 'Apply'}
                            </button>
                          </div>
                        </details>
                      )}
                    </div>
                  )}

                  {/* Discount line + total */}
                  <div className="flex items-center justify-between pt-2 border-t border-border">
                    <span className="font-heading text-lg font-bold text-text-primary">Total</span>
                    <span className="font-heading text-2xl font-bold text-primary" data-testid="booking-total">
                      ${(() => {
                        if (!promoValidation?.valid) return totalCost.toFixed(2);
                        const dv = parseFloat(promoValidation.discount_value || 0);
                        const isPercent = promoValidation.discount_type === 'percent';
                        const discountAmount = isPercent ? (totalCost * dv) / 100 : Math.min(dv, totalCost);
                        return Math.max(0, totalCost - discountAmount).toFixed(2);
                      })()}
                    </span>
                  </div>
                </div>

                <button
                  onClick={handleBooking}
                  disabled={user && user.role === 'customer' && !selectedPlaceId}
                  className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="book-cleaner-cta"
                >
                  {user && user.role === 'customer' && !selectedPlaceId ? 'Add an address to book' : 'Book Now'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CleanerProfilePage;
